import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Copy, Download, PauseCircle, RefreshCw, Trash2 } from 'lucide-react';
import { Button, ConfirmDialog, ErrorCard, SkeletonTable } from '@/components/ui';
import { PageHeader, Section, Stat } from '@/components/layout/PageLayout';
import { LicenseStatusBadge } from '@/components/tenants/LicenseStatusBadge';
import {
  useDeleteLicense, useLicense, useRegenerateLicenseKey, useToggleLicenseEnabled, useUpdateLicense,
} from '@/api/licenses';
import { getErrorMessage } from '@/lib/errors';
import { showToast } from '@/lib/toast';
import { downloadLicenseFile } from '@/lib/downloadLicenseFile';

function toDateInputValue(iso: string): string {
  return iso.slice(0, 10);
}

export function LicenseDetailPage() {
  const { tenantId = '' } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();

  const [confirmAction, setConfirmAction] = useState<'revoke' | 'regenerate' | 'toggle' | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({ endpointLimit: 0, endpointsUsed: 0, expiryDate: '' });

  const { data: license, isLoading, isError, error, refetch } = useLicense(tenantId);
  const { mutate: deleteLicense, isPending: isRevoking } = useDeleteLicense(tenantId);
  const { mutate: regenerateKey, isPending: isRegenerating } = useRegenerateLicenseKey(tenantId);
  const { mutate: toggleEnabled, isPending: isToggling } = useToggleLicenseEnabled();
  const { mutate: updateLicense, isPending: isSaving, error: updateError } = useUpdateLicense(tenantId);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader eyebrow="Governance" title="License" />
        <SkeletonTable rows={5} />
      </div>
    );
  }

  if (isError || !license) {
    return <ErrorCard title="Failed to load license" message={getErrorMessage(error)} onRetry={() => void refetch()} />;
  }

  const startEdit = () => {
    setDraft({
      endpointLimit: license.endpointLimit,
      endpointsUsed: license.endpointsUsed,
      expiryDate: toDateInputValue(license.expiryDate),
    });
    setIsEditing(true);
  };
  const cancelEdit = () => setIsEditing(false);
  const saveEdit = (e: FormEvent) => {
    e.preventDefault();
    updateLicense(
      {
        endpointLimit: draft.endpointLimit,
        endpointsUsed: draft.endpointsUsed,
        expiryDate: new Date(draft.expiryDate).toISOString(),
      },
      { onSuccess: () => { showToast('License updated successfully'); setIsEditing(false); } },
    );
  };

  const handleDownload = () => {
    if (!license.licenseKey) {
      showToast('Regenerate the key to enable downloading the full value', 'error');
      return;
    }
    downloadLicenseFile(license.tenantId, license.licenseKey);
  };

  const closeConfirm = () => setConfirmAction(null);

  const handleConfirm = () => {
    if (confirmAction === 'revoke') {
      deleteLicense(undefined, {
        onSuccess: () => {
          showToast('License revoked successfully');
          navigate('/license', { replace: true });
        },
        onError: closeConfirm,
      });
    } else if (confirmAction === 'regenerate') {
      regenerateKey(undefined, {
        onSuccess: () => { showToast('License key regenerated successfully'); closeConfirm(); },
        onError: closeConfirm,
      });
    } else if (confirmAction === 'toggle') {
      toggleEnabled(
        { tenantId, enabled: !license.enabled },
        {
          onSuccess: () => { showToast(license.enabled ? 'License disabled' : 'License enabled'); closeConfirm(); },
          onError: closeConfirm,
        },
      );
    }
  };

  const isConfirmLoading = isRevoking || isRegenerating || isToggling;

  const confirmDialogCopy = () => {
    switch (confirmAction) {
      case 'revoke':
        return {
          title: 'Revoke this license?',
          description: 'This immediately invalidates the license key. This action cannot be undone.',
          confirmLabel: 'Revoke',
          destructive: true,
        };
      case 'regenerate':
        return {
          title: 'Regenerate license key?',
          description: 'The current key will stop working immediately — anything using it will need the new key.',
          confirmLabel: 'Regenerate',
          destructive: false,
        };
      case 'toggle':
        return license.enabled
          ? {
              title: 'Disable this license?',
              description: 'Endpoints relying on this license may be affected while it is disabled. You can re-enable it at any time.',
              confirmLabel: 'Disable',
              destructive: true,
            }
          : {
              title: 'Enable this license?',
              description: 'This restores the license to its normal status based on its expiry date.',
              confirmLabel: 'Enable',
              destructive: false,
            };
      default:
        return { title: '', description: '', confirmLabel: 'Confirm', destructive: false };
    }
  };

  const confirmCopy = confirmDialogCopy();

  const inputCls =
    'w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-text-primary focus:border-border-focus focus:outline-none focus:ring-2 focus:ring-border-focus/20';

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Governance"
        title={`${license.tenantName} — License`}
        description={
          <button onClick={() => navigate('/license')} className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-text-primary">
            <ArrowLeft className="h-3 w-3" /> Back to licenses
          </button>
        }
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={license.enabled ? <PauseCircle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              onClick={() => setConfirmAction('toggle')}
            >
              {license.enabled ? 'Disable' : 'Enable'}
            </Button>
            <Button variant="secondary" size="sm" leadingIcon={<Download className="h-3.5 w-3.5" />} onClick={handleDownload}>
              Download license file
            </Button>
            <Button
              variant="danger"
              size="sm"
              leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={() => setConfirmAction('revoke')}
            >
              Revoke
            </Button>
          </div>
        }
      />

      <Section title="Tenant">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Stat label="Tenant ID" value={<span className="font-mono">{license.tenantId}</span>} />
          <Stat label="Name" value={license.tenantName} />
          <Stat
            label=" "
            value={
              <button onClick={() => navigate(`/tenant/${license.tenantId}`)} className="text-xs text-accent hover:underline">
                View tenant details
              </button>
            }
          />
        </div>
      </Section>

      <Section
        title="License"
        actions={
          isEditing ? (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={isSaving}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={saveEdit} loading={isSaving}>Save</Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={startEdit}>Edit</Button>
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<RefreshCw className="h-3.5 w-3.5" />}
                onClick={() => setConfirmAction('regenerate')}
              >
                Regenerate key
              </Button>
            </div>
          )
        }
      >
        {isEditing && updateError && (
          <div className="mb-3 rounded-md border border-state-danger/40 bg-state-danger/10 px-3 py-2 text-xs text-state-danger">
            {getErrorMessage(updateError)}
          </div>
        )}
        <form onSubmit={saveEdit} className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Status" value={<LicenseStatusBadge status={license.status} />} />
          {isEditing ? (
            <div className="space-y-1">
              <label htmlFor="edit-endpointLimit" className="block text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                Endpoint limit
              </label>
              <input
                id="edit-endpointLimit"
                type="number"
                min={0}
                required
                autoFocus
                value={draft.endpointLimit}
                onChange={(e) => setDraft((d) => ({ ...d, endpointLimit: Number(e.target.value) }))}
                className={inputCls}
              />
            </div>
          ) : (
            <Stat label="Endpoint limit" value={license.endpointLimit} />
          )}
          {isEditing ? (
            <div className="space-y-1">
              <label htmlFor="edit-endpointsUsed" className="block text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                Endpoints used
              </label>
              <input
                id="edit-endpointsUsed"
                type="number"
                min={0}
                required
                value={draft.endpointsUsed}
                onChange={(e) => setDraft((d) => ({ ...d, endpointsUsed: Number(e.target.value) }))}
                className={inputCls}
              />
            </div>
          ) : (
            <Stat label="Endpoints used" value={license.endpointsUsed} />
          )}
          {isEditing ? (
            <div className="space-y-1">
              <label htmlFor="edit-expiryDate" className="block text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
                Expiry date
              </label>
              <input
                id="edit-expiryDate"
                type="date"
                required
                value={draft.expiryDate}
                onChange={(e) => setDraft((d) => ({ ...d, expiryDate: e.target.value }))}
                className={inputCls}
              />
            </div>
          ) : (
            <Stat label="Expiry date" value={new Date(license.expiryDate).toLocaleDateString()} />
          )}
          <Stat
            label="License key"
            value={
              <button
                type="button"
                title={license.licenseKey ? 'Copy full license key' : 'Regenerate the key to enable copying'}
                className="group/key inline-flex cursor-pointer items-center gap-1.5 font-mono"
                onClick={async () => {
                  if (!license.licenseKey) {
                    showToast('Regenerate the key to enable copying the full value', 'error');
                    return;
                  }
                  await navigator.clipboard.writeText(license.licenseKey);
                  showToast('License key copied to clipboard');
                }}
              >
                •••• {license.licenseKeyLastFour}
                <Copy className="h-3 w-3 text-text-tertiary opacity-0 transition-opacity group-hover/key:opacity-100" />
              </button>
            }
          />
        </form>
      </Section>

      <ConfirmDialog
        open={confirmAction !== null}
        title={confirmCopy.title}
        description={confirmCopy.description}
        confirmLabel={confirmCopy.confirmLabel}
        destructive={confirmCopy.destructive}
        loading={isConfirmLoading}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
}
